import type { ObservableValue } from '@amazon/vinyl-observable'
import { data } from '@amazon/vinyl-observable'
import type { PlayQueueBindingDeps } from '@/playback/playQueueBinding'
import type {
    CloudQueueController,
    CreateCloudQueueOptions,
    Player,
    PlaylistController,
} from '@amzn/amp-playback'
import { createPartySession, endPartySession } from '@/party/partyService'
import { type QueueUpdatedResult, queueUpdatedSubscription } from '@/party/partyQueueService'
import {
    AbortError,
    type Disposable,
    EventHostImpl,
    type LogTarget,
    logDebug,
    logWarn,
    noop,
    type ReadonlyEventHost,
    substitute,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { subscribeToHermes } from '@/service/hermes/hermesSubscription'
import { ctx } from '@/util/context'

const { errorHandler } = ctx('PartySessionController')

/**
 * URL the QR code points guests at. Hardcoded to localhost:8084 for
 * the in-development guest web app — promote to a build-time config
 * once a real guest deploy exists.
 */
const PARTY_GUEST_URL = 'https://d1tiq20nmh0t6q.cloudfront.net/partysession/{sessionId}'

export type PartySessionStatus = 'active' | 'starting' | 'ending' | 'inactive'

/**
 * Events dispatched by `PartySessionController`. `queueChange` fires
 * each time Hermes pushes a queue update for the active session —
 * `event.data` is the `data` object from the underlying GraphQL
 * subscription. Status / session transitions are observable via
 * `status$` / `session$` so they don't need their own event.
 */
export interface PartySessionEventMap {
    readonly queueChange: QueueChangeEvent
}

export interface QueueChangeEvent {
    readonly data: QueueUpdatedResult
}

export interface PartySessionController extends ReadonlyEventHost<PartySessionEventMap> {
    readonly status$: ObservableValue<PartySessionStatus>
    readonly session$: ObservableValue<PartySession | null>

    /**
     * Starts a new party session. Ends any current session.
     */
    createSession(options: CreateCloudQueueOptions): Promise<PartySession>

    /**
     * Ends the current session, does nothing if no session is active.
     */
    endSession(): Promise<void>
}

/**
 * A party session.
 */
export interface PartySession {
    readonly sessionId: string
    readonly queueId: string
    readonly guestUrl: string
}

export type PartySessionControllerImplDeps = PlayQueueBindingDeps & {
    readonly cloudQueueController: CloudQueueController
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

export class PartySessionControllerImpl
    extends EventHostImpl<PartySessionEventMap>
    implements PartySessionController, LogTarget, Disposable
{
    // A monotonically incrementing id when creating a session
    private mdx = 0
    private readonly _status = data<PartySessionStatus>('inactive')
    private readonly _session = data<PartySession | null>(null)

    /**
     * Cleanup for the live Hermes subscription, if any. Cleared on
     * endSession() so guests adding to a stale queue can't push
     * `change` events at us after we've moved on.
     */
    private subscriptionOff: Unsubscribe | null = null

    get session$(): ObservableValue<PartySession | null> {
        return this._session
    }

    get status$(): ObservableValue<PartySessionStatus> {
        return this._status
    }

    constructor(private readonly deps: PartySessionControllerImplDeps) {
        super()
    }

    async createSession(options: CreateCloudQueueOptions): Promise<PartySession> {
        logDebug(this, 'createSession', options)
        await this.endSession()
        const my = ++this.mdx
        this._session.value = null
        this._status.value = 'starting'
        return (async (): Promise<PartySession> => {
            // Firefly's createQueue requires a non-empty playParams to bootstrap.
            // Lazy import keeps the queue-manipulation service out of the shell.
            const { createCloudQueue } = await import('@amzn/amp-playback/cloudQueueService')
            const queueResult = await createCloudQueue(options)
            const queueId = queueResult.createQueue?.queue?.id
            if (!queueId) {
                throw new Error('Could not create a queue')
            }
            this.deps.cloudQueueController.setQueue({
                queueResult,
                playParams: options.playParams,
                continueCurrent: true,
            })
            const session = await createPartySession(queueId)
            const sessionId = session.createPartySession?.id
            if (!sessionId) {
                throw new Error('Failed to create a party session')
            }

            return {
                sessionId,
                queueId,
                guestUrl: substitute(PARTY_GUEST_URL, { sessionId: encodeURIComponent(sessionId) }),
            }
        })()
            .then((newSession) => {
                if (my !== this.mdx) throw new AbortError()
                this._session.value = newSession
                this._status.value = 'active'
                this.subscribeToQueueUpdates(newSession)
                return newSession
            })
            .catch((e) => {
                if (my === this.mdx) this._status.value = 'inactive'
                throw e
            })
    }

    async endSession(): Promise<void> {
        logDebug(this, 'endSession')
        if (this._status.value === 'inactive') return
        ++this.mdx
        // Drop the Hermes subscription before sending endPartySession
        // — once the session is ended the gateway will push a final
        // event, but we don't want to surface it through `queueChange`.
        this.subscriptionOff?.()
        this.subscriptionOff = null
        const session = this._session.value
        if (session) {
            this._status.value = 'ending'
            this._session.value = null
            await endPartySession(session.sessionId).catch((e: Error) => {
                logWarn(this, 'endPartySession failed', e)
            })
        }
        this._status.value = 'inactive'
    }

    /**
     * Open a Hermes pub/sub subscription for the queue backing this
     * session. Each push triggers a `change` event with reason
     * `queueUpdated` so the UI / page can refetch the queue view.
     * The subscription is owned by this controller — endSession()
     * disposes it so we don't leak listeners when the host bounces
     * between sessions.
     */
    private subscribeToQueueUpdates(session: PartySession): void {
        const my = this.mdx
        const sub = subscribeToHermes({
            // Hermes uses the subscription field name (lowercased
            // operation, not the GraphQL doc name) for scope lookup.
            operationName: 'queueUpdated',
            query: queueUpdatedSubscription,
            variables: { queueId: session.queueId },
            onNext: (data) => {
                logDebug(this, 'queueUpdated', session.queueId, my === this.mdx)
                if (my !== this.mdx) return
                this.deps.playlistController.invalidateNext()
                this.dispatch('queueChange', { data })
            },
            onError: (message) => {
                if (my !== this.mdx) return
                errorHandler(new Error(`hermes subscribe failed: ${message}`))
            },
        })
        this.subscriptionOff = () => sub.dispose()
    }

    dispose(): void {
        this.endSession().catch(noop)
        super.dispose()
    }
}
